import { useMachine, normalizeProps, Portal } from "@zag-js/react";
import * as menu from "@zag-js/menu";
import {
  MarketsSort,
  MarketsSortField,
  ProtocolKey,
  SortBy,
  SortDirection,
} from "@silo/types";
import { getProtocolConfig } from "@silo/config";
import { ReactNode, useEffect } from "react";
import { getButtonClassNames } from "../button";
import { cn } from "../../utils/class-names";
import { CheckmarkIcon, ChevronRightIcon, SortIcon } from "../../icons";
import { match } from "ts-pattern";

interface SortMenuProps {
  className?: string;
  protocolKey: ProtocolKey;
  marketsSort: MarketsSort;
  sortBy: SortBy;
  resetSort: () => void;
}

export function SortMenu({
  className,
  protocolKey,
  marketsSort,
  sortBy,
  resetSort,
}: SortMenuProps): JSX.Element {
  const { firstBridgeSymbol, secondBridgeSymbol } =
    getProtocolConfig(protocolKey);

  const [sortMenuState, sortMenuSend, sortMenuMachine] = useMachine(
    menu.machine({
      id: "markets-sort-menu",
      "aria-label": "Sort Menu",
      positioning: { placement: "bottom-end" },
    })
  );
  const sortMenu = menu.connect(sortMenuState, sortMenuSend, normalizeProps);

  // sub-menus
  const [baseMenuState, baseMenuSend, baseMenuMachine] = useMachine(
    menu.machine({ id: "markets-sort-base-menu", "aria-label": "Base" })
  );
  const baseMenu = menu.connect(baseMenuState, baseMenuSend, normalizeProps);

  const [firstBridgeMenuState, firstBridgeMenuSend, firstBridgeMenuMachine] =
    useMachine(
      menu.machine({
        id: "markets-sort-first-bridge-menu",
        "aria-label": firstBridgeSymbol,
      })
    );
  const firstBridgeMenu = menu.connect(
    firstBridgeMenuState,
    firstBridgeMenuSend,
    normalizeProps
  );

  const [secondBridgeMenuState, secondBridgeMenuSend, secondBridgeMenuMachine] =
    useMachine(
      menu.machine({
        id: "markets-sort-second-bridge-menu",
        "aria-label": secondBridgeSymbol ?? undefined,
      })
    );
  const secondBridgeMenu = menu.connect(
    secondBridgeMenuState,
    secondBridgeMenuSend,
    normalizeProps
  );

  useEffect(() => {
    setTimeout(() => {
      sortMenu.setChild(baseMenuMachine);
      baseMenu.setParent(sortMenuMachine);

      sortMenu.setChild(firstBridgeMenuMachine);
      firstBridgeMenu.setParent(sortMenuMachine);

      sortMenu.setChild(secondBridgeMenuMachine);
      secondBridgeMenu.setParent(sortMenuMachine);
    });
    // the documentation excludes deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const baseMenuTriggerProps = sortMenu.getTriggerItemProps(baseMenu);
  const firstBridgeMenuTriggerProps =
    sortMenu.getTriggerItemProps(firstBridgeMenu);
  const secondBridgeMenuTriggerProps =
    sortMenu.getTriggerItemProps(secondBridgeMenu);

  const buttonClassNames = getButtonClassNames("secondary", "normal");

  const createFieldHandler = (field: MarketsSortField) => () => {
    if (!marketsSort) {
      return sortBy(field, "desc");
    }

    sortBy(field, marketsSort.direction);
  };

  const createDirectionHandler = (direction: "asc" | "desc") => () => {
    if (!marketsSort) {
      return sortBy("marketSymbol", direction);
    }

    sortBy(marketsSort.field, direction);
  };

  const isSortedAscending = marketsSort?.direction === "asc";

  return (
    <>
      <button
        className={cn(
          "flex gap-2.5 items-center !bg-shale-700 text-shale-200 font-normal px-4",
          buttonClassNames,
          className
        )}
        {...sortMenu.triggerProps}
      >
        <SortIcon
          className={`w-5 h-5 ${isSortedAscending ? "-scale-y-100" : ""}`}
        />
        {getTriggerCopy(marketsSort, protocolKey)}
      </button>

      <Portal>
        <div {...sortMenu.positionerProps}>
          <div
            {...sortMenu.contentProps}
            className="py-5 rounded-lg bg-shale-750 w-60"
          >
            <div className="mb-4">
              <div className="flex justify-between px-5 mb-3">
                <h2 className="text-base">Sort by</h2>

                <button className="text-blue-300" onClick={resetSort}>
                  Clear
                </button>
              </div>
              <ul className="flex flex-col text-shale-200">
                <FieldItem
                  marketsSort={marketsSort}
                  menu={sortMenu}
                  field="marketSymbol"
                  createFieldHandler={createFieldHandler}
                >
                  Market
                </FieldItem>

                <FieldItem
                  marketsSort={marketsSort}
                  menu={sortMenu}
                  field="liquidityUsd"
                  createFieldHandler={createFieldHandler}
                >
                  Available to borrow
                </FieldItem>

                <li
                  {...baseMenuTriggerProps}
                  className="px-5 py-2 duration-100 hover:bg-shale-600 hover:ease-in"
                >
                  <div className="flex items-center justify-between">
                    Base
                    <ChevronRightIcon className="w-6 h-6" />
                  </div>
                </li>

                <li
                  {...firstBridgeMenuTriggerProps}
                  className="px-5 py-2 duration-100 hover:bg-shale-600 hover:ease-in"
                >
                  <div className="flex items-center justify-between">
                    {firstBridgeSymbol}
                    <ChevronRightIcon className="w-6 h-6" />
                  </div>
                </li>

                <li
                  {...secondBridgeMenuTriggerProps}
                  className="px-5 py-2 duration-100 hover:bg-shale-600 hover:ease-in"
                >
                  <div className="flex items-center justify-between">
                    {secondBridgeSymbol}
                    <ChevronRightIcon className="w-6 h-6" />
                  </div>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="px-5 mb-3 text-base">Order</h2>
              <ul className="flex flex-col text-shale-200">
                <DirectionItem
                  marketsSort={marketsSort}
                  menu={sortMenu}
                  direction="desc"
                  createDirectionHandler={createDirectionHandler}
                />
                <DirectionItem
                  marketsSort={marketsSort}
                  menu={sortMenu}
                  direction="asc"
                  createDirectionHandler={createDirectionHandler}
                />
              </ul>
            </div>
          </div>
        </div>
      </Portal>

      <Portal>
        <div {...baseMenu.positionerProps}>
          <div {...baseMenu.contentProps}>
            <ul className="flex flex-col py-3 mx-1 rounded-lg bg-shale-750 w-60 text-shale-200">
              <FieldItem
                marketsSort={marketsSort}
                menu={baseMenu}
                field="baseDepositTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Deposit APR
              </FieldItem>
              <FieldItem
                marketsSort={marketsSort}
                menu={baseMenu}
                field="baseDebtTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Borrow APR
              </FieldItem>
            </ul>
          </div>
        </div>
      </Portal>

      <Portal>
        <div {...firstBridgeMenu.positionerProps}>
          <div {...firstBridgeMenu.contentProps}>
            <ul className="flex flex-col py-3 mx-1 rounded-lg bg-shale-750 w-60 text-shale-200">
              <FieldItem
                marketsSort={marketsSort}
                menu={firstBridgeMenu}
                field="firstBridgeDepositTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Deposit APR
              </FieldItem>
              <FieldItem
                marketsSort={marketsSort}
                menu={firstBridgeMenu}
                field="firstBridgeDebtTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Borrow APR
              </FieldItem>
            </ul>
          </div>
        </div>
      </Portal>

      <Portal>
        <div {...secondBridgeMenu.positionerProps}>
          <div {...secondBridgeMenu.contentProps}>
            <ul className="flex flex-col py-3 mx-1 rounded-lg bg-shale-750 w-60 text-shale-200">
              <FieldItem
                marketsSort={marketsSort}
                menu={secondBridgeMenu}
                field="secondBridgeDepositTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Deposit APR
              </FieldItem>
              <FieldItem
                marketsSort={marketsSort}
                menu={secondBridgeMenu}
                field="secondBridgeDebtTotalApr"
                createFieldHandler={createFieldHandler}
              >
                Borrow APR
              </FieldItem>
            </ul>
          </div>
        </div>
      </Portal>
    </>
  );
}

type Menu = ReturnType<typeof menu.connect>;

interface FieldItemProps {
  marketsSort: MarketsSort;
  menu: Menu;
  field: MarketsSortField;
  createFieldHandler: (field: MarketsSortField) => () => void;
  children?: ReactNode;
}

export function FieldItem({
  marketsSort,
  menu,
  field,
  createFieldHandler,
  children,
}: FieldItemProps): JSX.Element {
  const isSelected = marketsSort?.field === field;
  const classNames = isSelected ? "text-white font-medium" : "";
  const onClick = createFieldHandler(field);

  return (
    <li
      {...menu.getItemProps({ id: `sort-menu-${field}` })}
      className="px-5 py-2 cursor-pointer"
      onClick={onClick}
    >
      <div className={`flex items-center justify-between ${classNames}`}>
        {children}
        {isSelected && <CheckmarkIcon className="w-6 h-6" aria-hidden />}
      </div>
    </li>
  );
}

interface DirectionItemProps {
  marketsSort: MarketsSort;
  menu: Menu;
  direction: SortDirection;
  createDirectionHandler: (direction: SortDirection) => () => void;
}

function DirectionItem({
  marketsSort,
  menu,
  direction,
  createDirectionHandler,
}: DirectionItemProps): JSX.Element {
  const isSelected = marketsSort?.direction === direction;
  const classNames = isSelected ? "text-white font-medium" : "";
  const onClick = createDirectionHandler(direction);

  return (
    <li
      {...menu.getItemProps({ id: `sort-menu-${direction}` })}
      className="px-5 py-2 cursor-pointer"
      onClick={onClick}
    >
      <div className={`flex items-center justify-between ${classNames}`}>
        {getDirectionCopy(direction, marketsSort)}
        {isSelected && <CheckmarkIcon className="w-6 h-6" aria-hidden />}
      </div>
    </li>
  );
}

function getTriggerCopy(
  marketsSort: MarketsSort,
  protocolKey: ProtocolKey
): string {
  if (!marketsSort) return "Sort";

  const { firstBridgeSymbol, secondBridgeSymbol } =
    getProtocolConfig(protocolKey);

  const triggerCopyMap: Record<MarketsSortField, string> = {
    marketSymbol: "Market",
    liquidityUsd: "Available to borrow",
    baseDepositTotalApr: "Base: Deposit APR",
    baseDebtTotalApr: "Base: Borrow APR",
    firstBridgeDepositTotalApr: `${firstBridgeSymbol}: Deposit APR`,
    firstBridgeDebtTotalApr: `${firstBridgeSymbol}: Borrow APR`,
    secondBridgeDepositTotalApr: `${secondBridgeSymbol}: Deposit APR`,
    secondBridgeDebtTotalApr: `${secondBridgeSymbol}: Borrow APR`,
  };

  return triggerCopyMap[marketsSort.field];
}

function getDirectionCopy(
  direction: SortDirection,
  marketsSort: MarketsSort
): string {
  return match({ direction, marketsSort })
    .with(
      { direction: "desc", marketsSort: { field: "marketSymbol" } },
      () => "A to Z"
    )
    .with(
      { direction: "asc", marketsSort: { field: "marketSymbol" } },
      () => "Z to A"
    )
    .with({ direction: "desc" }, () => "High to low")
    .with({ direction: "asc" }, () => "Low to high")
    .exhaustive();
}
